import { Day, Month } from '@synergy/models';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import {
    FormikPoolTimePicker,
    PoolButton,
    PoolFormikSelect,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
} from '@synergy/components-next';
import { Grid } from '@mui/material';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolTextArea, SelectOption } from '@synergy/components';
import { ReactElement } from 'react';
import { fields } from '@synergy/utils';

import { ImportIntervalType, ImportReceptionType, ImportSupplier } from '../../models/import-supplier';
import { ImportSupplierFormikValues } from '../../models/import-supplier-formik-values';
import { PoolImportSupplierAutocompleteList } from '../../../../../common/components/autocomplete-list/import-supplier-autocomplete-list/pool-import-supplier-autocomplete-list';
import { Supplier } from '../../../../../common/models/supplier';
import { addOrUpdateImportSuppliers } from '../../store/suppliers/import-suppliers-actions';
import { importSupplierAddValidationSchema } from './import-overview-add-supplier-validation-schema';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export interface AddSupplierFormValues {
    supplier: Supplier | null;
}

export interface AddSupplierModalProps {
    importSupplier: ImportSupplier | null;
    open: boolean;
    onClose: () => void;
}

export const AddSupplierModal = (props: AddSupplierModalProps): ReactElement => {
    const { open, onClose, importSupplier } = props;
    const dispatch = useThunkDispatch();
    const fieldNames = fields<ImportSupplierFormikValues>();

    const handleSubmit = (values: ImportSupplierFormikValues) => {
        onClose();
        dispatch(addOrUpdateImportSuppliers({ importSupplier: values }));
    };

    const initialValues: ImportSupplierFormikValues = {
        id: importSupplier?.id ?? undefined,
        supplier: importSupplier?.supplier ?? null,
        importIntervalType: importSupplier?.importIntervalType ?? null,
        time: importSupplier?.time ?? null,
        weekday: importSupplier?.weekday ?? undefined,
        dayOfMonth: importSupplier?.dayOfMonth ?? undefined,
        month: importSupplier?.month ?? undefined,
        importReceptionType: importSupplier?.importReceptionType ?? null,
        internalNote: importSupplier?.internalNote ?? '',
    };

    const weeklyOptions = Object.values(Day).map<SelectOption>((day, index) => ({
        value: index + 1,
        displayValue: <FormattedMessage id={`app.enum.day.${day}`} />,
    }));
    const monthlyOptions = Object.values(Month).map<SelectOption>((month, index) => ({
        value: index + 1,
        displayValue: <FormattedMessage id={`app.enum.month.${month}`} />,
    }));
    const dayOfMonthOptions = Array.from({ length: 31 }, (_, index) => ({
        value: index + 1,
        displayValue: (
            <FormattedMessage id={`app.imports.importSupplier.modal.dayOfMonthValue`} values={{ number: index + 1 }} />
        ),
    }));
    const importIntervalTypeOptions = Object.values(ImportIntervalType).map<SelectOption>(interval => ({
        value: interval,
        displayValue: <FormattedMessage id={`app.enum.importIntervalType.${interval}`} />,
    }));
    const importReceptionTypeOptions = Object.values(ImportReceptionType).map<SelectOption>(setting => ({
        value: setting,
        displayValue: <FormattedMessage id={`app.enum.importReceptionType.${setting}`} />,
    }));

    return (
        <PaloNextThemeProvider>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                enableReinitialize={true}
                validationSchema={importSupplierAddValidationSchema}
            >
                {(formikProps: FormikProps<ImportSupplierFormikValues>) => (
                    <Form>
                        <PoolModal open={open} onClose={onClose}>
                            <PoolModalTitle>
                                {importSupplier?.id ? (
                                    <FormattedMessage id="app.imports.importSupplier.modal.title.edit" />
                                ) : (
                                    <FormattedMessage id="app.imports.importSupplier.modal.title.add" />
                                )}
                            </PoolModalTitle>
                            <PoolModalContent>
                                <Field name={fieldNames.supplier}>
                                    {({ field, form }: FieldProps<Supplier>) => (
                                        <PoolImportSupplierAutocompleteList
                                            label={<FormattedMessage id="app.label.supplierAccount" />}
                                            onSelected={supplier => form.setFieldValue(fieldNames.supplier, supplier)}
                                            initialValue={importSupplier?.supplier.name}
                                            readOnly={!!importSupplier?.id}
                                            required={true}
                                            disabled={!!importSupplier?.id}
                                        />
                                    )}
                                </Field>

                                <Grid container={true} spacing={2} sx={{ mt: 1 }}>
                                    <Grid item={true} xs={12}>
                                        <PoolFormikSelect
                                            name={fieldNames.importIntervalType}
                                            fullWidth={true}
                                            options={importIntervalTypeOptions}
                                            label={
                                                <FormattedMessage id={'app.imports.importSupplier.modal.interval'} />
                                            }
                                        />
                                    </Grid>
                                    {formikProps.values.importIntervalType === ImportIntervalType.Daily && (
                                        <Grid item={true} xs={12}>
                                            <FormikPoolTimePicker
                                                name={fieldNames.time}
                                                label={<FormattedMessage id="app.label.time" />}
                                                required={true}
                                            />
                                        </Grid>
                                    )}

                                    {formikProps.values.importIntervalType &&
                                        [ImportIntervalType.Weekly, ImportIntervalType.EveryTwoWeeks].includes(
                                            formikProps.values.importIntervalType
                                        ) && (
                                            <Grid item={true} xs={12}>
                                                <PoolFormikSelect
                                                    name={fieldNames.weekday}
                                                    fullWidth={true}
                                                    options={weeklyOptions}
                                                    label={
                                                        <FormattedMessage
                                                            id={'app.imports.importSupplier.modal.weekday'}
                                                        />
                                                    }
                                                    required={true}
                                                />
                                            </Grid>
                                        )}

                                    {formikProps.values.importIntervalType === ImportIntervalType.Monthly && (
                                        <Grid item={true} xs={12}>
                                            <PoolFormikSelect
                                                name={fieldNames.dayOfMonth}
                                                fullWidth={true}
                                                options={dayOfMonthOptions}
                                                label={
                                                    <FormattedMessage
                                                        id={'app.imports.importSupplier.modal.dayOfMonth'}
                                                    />
                                                }
                                                required={true}
                                            />
                                        </Grid>
                                    )}

                                    {formikProps.values.importIntervalType === ImportIntervalType.Yearly && (
                                        <>
                                            <Grid item={true} xs={6}>
                                                <PoolFormikSelect
                                                    name={fieldNames.month}
                                                    fullWidth={true}
                                                    options={monthlyOptions}
                                                    label={
                                                        <FormattedMessage
                                                            id={'app.imports.importSupplier.modal.month'}
                                                        />
                                                    }
                                                    required={true}
                                                />
                                            </Grid>
                                            <Grid item={true} xs={6}>
                                                <PoolFormikSelect
                                                    name={fieldNames.weekday}
                                                    fullWidth={true}
                                                    options={weeklyOptions}
                                                    label={
                                                        <FormattedMessage
                                                            id={'app.imports.importSupplier.modal.weekday'}
                                                        />
                                                    }
                                                    required={true}
                                                />
                                            </Grid>
                                        </>
                                    )}

                                    <Grid item={true} xs={12}>
                                        <PoolFormikSelect
                                            name={fieldNames.importReceptionType}
                                            fullWidth={true}
                                            options={importReceptionTypeOptions}
                                            label={
                                                <FormattedMessage
                                                    id={'app.imports.suppliersTable.importReceptionType'}
                                                />
                                            }
                                        />
                                    </Grid>

                                    <Grid item={true} xs={12}>
                                        <Field name={fieldNames.internalNote}>
                                            {({ field, meta }: FieldProps<string>) => (
                                                <PoolTextArea
                                                    label={<FormattedMessage id="app.label.internalComment" />}
                                                    fullWidth={true}
                                                    rows="3"
                                                    rowsMax="50"
                                                    error={!!meta.touched && !!meta.error}
                                                    helperText={
                                                        meta.touched && meta.error ? (
                                                            <FormattedMessage id={meta.error} values={{ value: 300 }} />
                                                        ) : null
                                                    }
                                                    {...field}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                </Grid>
                            </PoolModalContent>
                            <PoolModalActions>
                                <PoolButton variant="outlined" onClick={onClose}>
                                    <FormattedMessage id="app.common.cancel" />
                                </PoolButton>
                                <PoolButton onClick={() => formikProps.handleSubmit()}>
                                    <FormattedMessage
                                        id={props.importSupplier?.id ? 'app.common.save' : 'app.common.add'}
                                    />
                                </PoolButton>
                            </PoolModalActions>
                        </PoolModal>
                    </Form>
                )}
            </Formik>
        </PaloNextThemeProvider>
    );
};
